import { useCallback, useEffect, useState } from 'react';

/**
 * Use this hook to run code on blur that comes after another state changing event.
 *
 * For example, if a keydown changes a state prop, and blurs a text element, the onBlur for the
 * text element will fire with the previous value of the state prop, because it was memoized and
 * didn't recompute before the event fired it.
 *
 * This hook gets around that by having the onBlur change another state variable that a useEffect
 * hook fires, which gives the callback memoized function a chance to recompute before firing.
 */
export function useOnBlurUpdate(onBlurCallback: () => void): () => void {
    const [blurred, setBlurred] = useState(false);

    useEffect(
        function delayedOnBlur() {
            if (blurred) {
                setBlurred(false);
                onBlurCallback();
            }
        },
        [onBlurCallback, blurred, setBlurred]
    );

    return useCallback(
        function onBlur() {
            setBlurred(true);
        },
        [setBlurred]
    );
}
